// Phase-3 Slice 35 — in-memory rename undo log.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 35.
//
// Records each successfully-applied `RenameBatch` per connection so the user
// can pop the last entry and run its `undoBatch` via `executeRename`. NAV9 —
// no hidden snapshots: the log only stores the batch the planner already
// produced.
//
// Bound: 10 most-recent entries per connection; older entries fall off the
// front. Session-only (module memory) — there's no persistence requirement
// for v1; a fresh app launch starts with an empty log.
//
// Mirrors `renameTabNoticeStore`'s Zustand pattern so callers can subscribe
// for "Undo last rename" command palette enabled-state.

import { create } from 'zustand';

import type { RenameBatch } from '../types';

export type RenameUndoEntry = {
  readonly batch: RenameBatch;
  readonly appliedAt: number;
  /** The database the rename actually RAN in — `renameProvider` routes F2 at
   *  the database the cursor's chain named, so that is not necessarily the
   *  database of whatever tab is active when the user undoes. Undo re-executes
   *  the reverse ALTERs, which on SQL Server are a bare
   *  `EXEC sp_rename '[dbo].[users]', …` with no database of their own, so
   *  replaying them against the active tab's database would rename a
   *  same-named object in the wrong database. Optional: an entry recorded
   *  before this field existed (or by a caller that has no database) falls back
   *  to the undo caller's. */
  readonly database?: string;
};

const MAX_ENTRIES_PER_CONNECTION = 10;

type State = {
  /** connectionId → entries, oldest first. The last entry is the next undo
   *  target. */
  readonly byConnectionId: Readonly<Record<string, readonly RenameUndoEntry[]>>;
};

type Actions = {
  /** Push a new applied-rename entry. Trims to the bound. `database` is the
   *  one the rename ran in — see {@link RenameUndoEntry}. */
  readonly recordApply: (connectionId: string, batch: RenameBatch, database?: string) => void;
  /** Pop the most-recent entry for the connection (mutating). Returns
   *  `undefined` if the log is empty. */
  readonly popLast: (connectionId: string) => RenameUndoEntry | undefined;
  /** Read the most-recent entry without popping. */
  readonly peekLast: (connectionId: string) => RenameUndoEntry | undefined;
  /** Whether there is at least one entry available to undo. */
  readonly canUndo: (connectionId: string) => boolean;
  /** Drop everything for one connection (e.g. on disconnect — wired in
   *  Slice 23 follow-ups). */
  readonly clearForConnection: (connectionId: string) => void;
  /** Drop the entire log (test-only). */
  readonly clearAll: () => void;
};

const initial: State = { byConnectionId: {} };

export const useRenameUndoLog = create<State & Actions>((set, get) => ({
  ...initial,

  recordApply: (connectionId, batch, database) => {
    set((state) => {
      const existing = state.byConnectionId[connectionId] ?? [];
      const next = [...existing, { batch, appliedAt: Date.now(), database }];
      const trimmed =
        next.length > MAX_ENTRIES_PER_CONNECTION
          ? next.slice(next.length - MAX_ENTRIES_PER_CONNECTION)
          : next;
      return {
        byConnectionId: { ...state.byConnectionId, [connectionId]: trimmed },
      };
    });
  },

  popLast: (connectionId) => {
    const entries = get().byConnectionId[connectionId] ?? [];
    if (entries.length === 0) return undefined;
    const last = entries[entries.length - 1];
    set((state) => {
      const remaining = entries.slice(0, -1);
      const next = { ...state.byConnectionId };
      if (remaining.length === 0) delete next[connectionId];
      else next[connectionId] = remaining;
      return { byConnectionId: next };
    });
    return last;
  },

  peekLast: (connectionId) => {
    const entries = get().byConnectionId[connectionId] ?? [];
    return entries.length === 0 ? undefined : entries[entries.length - 1];
  },

  canUndo: (connectionId) => {
    const entries = get().byConnectionId[connectionId] ?? [];
    return entries.length > 0;
  },

  clearForConnection: (connectionId) => {
    set((state) => {
      if (!(connectionId in state.byConnectionId)) return state;
      const next = { ...state.byConnectionId };
      delete next[connectionId];
      return { byConnectionId: next };
    });
  },

  clearAll: () => set(initial),
}));

export const RENAME_UNDO_LOG_MAX_PER_CONNECTION = MAX_ENTRIES_PER_CONNECTION;
